import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import {
  AlteracaoNcmEmLoteContextData,
  FormNcmLoteType,
  RowTableNCMEmLote,
} from '../types/context';
import { toast } from 'react-toastify';
import api from '~/services/api';
import { fetchLojasInfo } from '../utils/fetchLojasInfo';
import { validateNcmForSelection } from '../utils/validateNcmForSelection';

const DEFAULT_LOJAS: number[] = [1];
const DEFAULT_LOADING = { table: false, submit: false };

export const AlteracaoNcmEmLoteContext = createContext<
  AlteracaoNcmEmLoteContextData | undefined
>(undefined);

export const AlteracaoNcmEmLoteProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [lojas, setLojas] = useState<number[]>(DEFAULT_LOJAS);
  const [loading, setLoading] = useState(DEFAULT_LOADING);
  const [rows, setRows] = useState<RowTableNCMEmLote[]>([]);
  const [resetCampo, setResetCampo] = useState(false);

  const {
    register,
    getValues,
    handleSubmit,
    formState,
    control,
    setValue,
    watch,
    reset,
    setFocus,
    clearErrors,
    setError,
  } = useForm({
    reValidateMode: 'onBlur',
  });

  const formNcmLote = useMemo<FormNcmLoteType>(
    () => ({
      register,
      getValues,
      handleSubmit,
      formState,
      control,
      setValue,
      watch,
      reset,
      setFocus,
      clearErrors,
      setError,
    }),
    [
      register,
      getValues,
      handleSubmit,
      formState,
      control,
      setValue,
      watch,
      reset,
      setFocus,
      clearErrors,
      setError,
    ],
  );

  const handleChangeLojas = useCallback((value: number | number[]) => {
    setLojas(Array.isArray(value) ? value : [value]);
  }, []);

  const handleCheckProduct = useCallback((uuid: string) => {
    setRows((prev) =>
      prev.map((produto) =>
        produto.uuid === uuid
          ? { ...produto, flg_transferir: !produto.flg_transferir }
          : produto,
      ),
    );
  }, []);

  const handleCheckAllProducts = useCallback((check: boolean) => {
    setRows((prev) =>
      prev.map((produto) => ({ ...produto, flg_transferir: check })),
    );
  }, []);

  const onResetCampo = useCallback((value: boolean) => {
    setResetCampo(value);
  }, []);

  const handleFocusBuscaProduto = useCallback(() => {
    const buscaProduto = document.getElementById('busca-produto-326');

    if (buscaProduto) {
      setTimeout(() => {
        buscaProduto.click();
        buscaProduto.focus();
      }, 100);
    }
  }, []);

  const onClearFields = useCallback(() => {
    reset({
      busca_produto: [{ label: undefined, value: undefined }],
      cod_ncm: { label: undefined, value: undefined, cod_tipo_item: undefined },
      cod_serie: { label: 'Produto', value: 0 },
    });
    setLojas(DEFAULT_LOJAS);
    setRows([]);
    setLoading(DEFAULT_LOADING);
    setResetCampo(true);
    handleFocusBuscaProduto();
  }, [handleFocusBuscaProduto, reset]);

  useEffect(() => {
    onClearFields();
  }, []);

  const onTransfer = handleSubmit(async (data) => {
    const selectedRows = rows.filter(({ flg_transferir }) => flg_transferir);

    if (selectedRows.length === 0) {
      toast.warn('Ao menos um produto deve ter sido marcado para transferir');
      return;
    }

    const novoNcm = data?.cod_ncm?.value;

    if (!novoNcm) {
      toast.warn('O campo "Novo NCM" é obrigatório');
      return;
    }

    setLoading((prev) => ({ ...prev, table: false, submit: true }));

    try {
      const lojasInfo = await fetchLojasInfo(lojas);

      const isValid = await validateNcmForSelection({
        selectedRows,
        lojasInfo,
        novoNcm,
        lojas,
      });

      if (!isValid) return;

      const produtosForUpdate = selectedRows.map(
        ({ cod_loja, cod_produto }) => ({
          cod_loja,
          cod_produto,
          cod_ncm: novoNcm,
        }),
      );

      const { data: resData } = await api.put<{
        success: boolean;
        message: string;
      }>('/alteracao-ncm-em-lote', { produtosForUpdate });

      if (resData?.success) {
        toast.success(resData.message);
        onClearFields();
        return;
      }

      toast.warn(
        resData?.message ?? 'Não foi possível concluir a transferência.',
      );
    } catch (error) {
      toast.error('Não foi possível transferir o NCM. Tente novamente.');
    } finally {
      setLoading((prev) => ({ ...prev, table: false, submit: false }));
    }
  });

  const value = useMemo<AlteracaoNcmEmLoteContextData>(
    () => ({
      formNcmLote,
      handleChangeLojas,
      onClearFields,
      lojas,
      rows,
      setLoading,
      loading,
      setRows,
      handleCheckProduct,
      handleCheckAllProducts,
      onTransfer,
      onResetCampo,
      resetCampo,
      handleFocusBuscaProduto,
    }),
    [
      formNcmLote,
      handleChangeLojas,
      onClearFields,
      lojas,
      rows,
      setLoading,
      loading,
      setRows,
      handleCheckProduct,
      handleCheckAllProducts,
      onTransfer,
      onResetCampo,
      resetCampo,
      handleFocusBuscaProduto,
    ],
  );

  return (
    <AlteracaoNcmEmLoteContext.Provider value={value}>
      {children}
    </AlteracaoNcmEmLoteContext.Provider>
  );
};
